// 画面の枠：上部バー（ロゴ・資源4種・主Lv・メニュー）、PCの左ナビ（＋主Lv・経験値・SP）、スマホの下タブ、メニューの引き出し。
// 同じ導線を二重に置かない：PC は左ナビ＝主要画面、上部バー＝任務・プレゼント・お知らせ・メニュー（あそびかた・購入履歴・設定）。
// スマホは下タブ＝主要5画面、上部バーのメニュー＝それ以外。ショップは TOP の札とメニュー（PC・スマホとも）から。
// 編成のアイコンは出撃キャラの顔。中身は state から描く（独自のコピーを持たない）。
import { canSuggestInstall, installApp } from './pwa';
import { CHAR_IDS, linePointsOf, skillCapacity, skillLines, unspentLinePoints, type CharId } from '@hyakki/core';
import { currentName, go, type Screen } from '../router';
import { state, subscribe } from '../state';
import { levelInfo } from '../core-bridge';
import { $, esc, fmt, resIcon, until, wrapLabel } from './dom';
import { openStaminaInfo } from './stamina-info';
import { chatUnread, toggleChat } from './chat';
import { faceStyle } from '../assets';
import { avatarHtml, myAvatar, openAvatarPicker } from './avatar';
import { artIcon, icon } from './icons';
import { closeAllModals, openModal } from './overlay';
import { openContact } from './contact';
import { isMobileLayout, sideNav } from './viewport';

/** 狭い資源バー用。切り捨てで所持数を多く見せず、正確な数はタイトルにも残す。 */
function resourceValue(n: number | undefined) {
  const short = n == null ? '—' : n >= 100_000_000 ? `${Math.floor(n / 100_000_000)}億` : n >= 10_000 ? `${Math.floor(n / 10_000)}万` : fmt(n);
  return `<b title="${fmt(n)}" aria-label="${fmt(n)}"><span class="res-full">${fmt(n)}</span><span class="res-short" aria-hidden="true">${short}</span></b>`;
}

/** PC 左ナビ */
export const NAV = [
  { id: 'top', label: 'トップ', icon: 'home' },
  { id: 'chara', label: '編成', icon: 'chara' },
  { id: 'stages', label: '出撃', icon: 'sortie' },
  { id: 'enhance', label: '強化', icon: 'enhance' },
  { id: 'village', label: '里', icon: 'village' },
  { id: 'gacha', label: 'おまもりガチャ', icon: 'gacha' },
  { id: 'dex', label: '図鑑', icon: 'dex' },
  { id: 'stories', label: '物語', icon: 'history' },
  { id: 'ranking', label: '番付', icon: 'rank' },
] as const;
/** PC 左ナビの中に開く、画面の中のタブ（アコーディオン）。スマホでは各画面の下にタブの列として出す */
const NAV_SUB: Record<string, { id: string; label: string }[]> = {
  chara: [
    { id: 'stats', label: '能力値' },
    { id: 'equip', label: '装備' },
    { id: 'skill', label: 'スキル' },
  ],
  enhance: [
    { id: 'equip', label: '装備' },
    { id: 'omamori', label: 'お守り' },
    { id: 'shiki', label: '式神' },
    { id: 'blueprint', label: '建物図面' },
  ],
};
/** 画面の中のタブが変わった（左ナビの開いた中の印を合わせる） */
export function setSubTab(screen: string, tab: string) {
  for (const a of document.querySelectorAll<HTMLElement>('[data-sub]')) {
    const on = a.dataset.sub === `${screen}:${tab}`;
    a.classList.toggle('on', on);
    if (on) a.setAttribute('aria-current', 'true');
    else a.removeAttribute('aria-current');
  }
}
/** スマホ下タブ */
const TABS = [
  { id: 'top', label: 'トップ', icon: 'home' },
  { id: 'chara', label: '編成', icon: 'chara' },
  { id: 'stages', label: '出撃', icon: 'sortie' },
  { id: 'enhance', label: '強化', icon: 'enhance' },
  { id: 'village', label: '里', icon: 'village' },
] as const;
/** メニューの引き出し。sp＝スマホだけ（PC は左ナビ・上部バーにあるもの） */
const MENU = [
  { id: 'shop', label: 'ショップ', icon: 'shop', sp: false },
  { id: 'gacha', label: 'おまもりガチャ', icon: 'gacha', sp: true },
  { id: 'dex', label: '百鬼図鑑', icon: 'dex', sp: true },
  { id: 'stories', label: '物語', icon: 'history', sp: true },
  { id: 'ranking', label: '番付', icon: 'rank', sp: true },
  { id: 'missions', label: '任務', icon: 'mission', sp: true },
  { id: 'presents', label: 'プレゼント', icon: 'present', sp: true },
  { id: 'news', label: 'お知らせ・イベント', icon: 'bell', sp: true },
  { id: 'help', label: 'あそびかた', icon: 'scroll', sp: false },
  // 画面ではなく小窓（要望・不具合報告・送った内容）
  { id: 'contact', label: 'お問い合わせ・要望・報告', icon: 'chat', sp: false },
  { id: 'purchases', label: '購入履歴', icon: 'history', sp: false },
  { id: 'settings', label: '設定', icon: 'gear', sp: false },
] as const;

let navKey = 'top';
/** ナビのアイコン。編成だけは出撃キャラの顔 */
const navIcon = (name: string) => (name === 'chara' ? `<i class="ic ic-face" style="${faceStyle(state.oshi)}" aria-hidden="true"></i>` : artIcon(name));
const mobile = () => isMobileLayout();

export function initShell() {
  const top = $('#topbar')!;
  const side = $('#sidenav')!;
  const tab = $('#tabbar')!;
  side.innerHTML = `<ul>${NAV.map(
    (n) => `<li class="${NAV_SUB[n.id] ? 'has-sub' : ''}" data-nav-li="${n.id}"><a href="#/${n.id}" data-nav="${n.id}">${navIcon(n.icon)}<span${n.label.length > 5 ? ' class="long"' : ''}>${wrapLabel(n.label)}</span>${NAV_SUB[n.id] ? icon('down', 'sn-chev') : ''}</a>${
      NAV_SUB[n.id] ? `<ul class="sn-sub">${NAV_SUB[n.id].map((t) => `<li><a href="#/${n.id}?tab=${t.id}" data-sub="${n.id}:${t.id}">${t.label}</a></li>`).join('')}</ul>` : ''
    }</li>`,
  ).join('')}</ul>
    <div class="side-lv panel-navy" aria-label="主のレベル"></div>`;
  tab.innerHTML = TABS.map((t) => `<button class="tab" data-tab="${t.id}">${navIcon(t.icon)}<span>${t.label}</span></button>`).join('');
  // 左メニューで別の項目を押したら、開いている窓（式神の詳細など）は閉じる。PC では窓の外の左メニューも押せるため
  side.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('a[href]')) closeAllModals();
  });
  tab.addEventListener('click', (e) => {
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-tab]');
    if (!b) return;
    closeAllModals();
    document.getElementById('app')!.classList.remove('chat-mobile-open');
    go(b.dataset.tab!);
  });
  top.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('[data-menu]')) return openMenu();
    if (t.closest('[data-chatbtn]')) return toggleChat();
    if (t.closest('[data-stamina]')) return openStaminaInfo();
    const b = t.closest<HTMLElement>('[data-go]');
    if (!b) return;
    closeAllModals();
    go(b.dataset.go!);
  });
  renderTop();
  renderSideLv();
  subscribe((ch) => {
    if (ch.has('resources') || ch.has('me') || ch.has('stamina') || ch.has('oshi') || ch.has('online') || ch.has('pendingOrder')) renderTop();
    if (ch.has('resources') || ch.has('me') || ch.has('profile') || ch.has('oshi')) renderSideLv();
    if (ch.has('oshi')) for (const f of document.querySelectorAll<HTMLElement>('.ic-face')) f.setAttribute('style', faceStyle(state.oshi));
  });
  setInterval(renderTop, 30_000); // 兵糧の回復時刻の表示
}

export function updateNav(name: string, s: Screen) {
  navKey = s.nav ?? name;
  const app = document.getElementById('app')!;
  app.dataset.screen = name;
  app.classList.toggle('full', !!s.full);
  app.classList.toggle('authed', !!state.me && name !== 'login');
  for (const a of document.querySelectorAll<HTMLElement>('[data-nav]')) {
    a.classList.toggle('active', a.dataset.nav === navKey);
    if (a.dataset.nav === navKey) a.setAttribute('aria-current', 'page');
    else a.removeAttribute('aria-current');
  }
  for (const a of document.querySelectorAll<HTMLElement>('[data-tab]')) a.classList.toggle('active', a.dataset.tab === navKey);
  // アコーディオン：今の画面の項目だけ開く
  for (const li of document.querySelectorAll<HTMLElement>('[data-nav-li]')) li.classList.toggle('open', li.dataset.navLi === navKey);
}

export function renderUnreadChat() {
  const n = chatUnread();
  for (const b of document.querySelectorAll<HTMLElement>('.chat-btn .badge')) {
    b.hidden = n === 0;
    b.textContent = n > 99 ? '99+' : String(n);
  }
}

/** 左のポイントを見せるキャラ（編成・スキルの画面が開いているキャラ。ほかの画面では出撃キャラ） */
let pointsChar: CharId | null = null;
export function setPointsChar(c: CharId | null) {
  if (pointsChar === c) return;
  pointsChar = c;
  renderSideLv();
}

function lvData() {
  const me = state.me;
  const lv = levelInfo(state.resources.exp ?? 0, state.profile) ?? me?.level;
  // スキルのポイント：系統のキャラは「残り/合計」（残りはそのキャラの振っていない分）、無ければ装着できるコストの合計
  const p = state.profile;
  const lines = !!p && CHAR_IDS.some((c) => skillLines(c));
  const c = pointsChar ?? state.oshi;
  const sp = p ? (lines ? { left: unspentLinePoints(p).find((x) => x.charId === c)?.left ?? 0, total: linePointsOf(p) } : { left: null, total: skillCapacity(p).total }) : null;
  return { lv, sp };
}

function renderSideLv() {
  const box = document.querySelector<HTMLElement>('.side-lv');
  if (!box) return;
  if (!state.me) return void (box.innerHTML = '');
  const { lv, sp } = lvData();
  const pct = lv?.need ? Math.min(100, (lv.into / lv.need) * 100) : 100;
  box.innerHTML = `<div class="lv">主Lv.<b>${lv?.level ?? '—'}</b></div>
    <div class="meter" role="progressbar" aria-label="経験値" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(pct)}"><i style="width:${pct}%"></i></div>
    <div class="nx"><span>次のレベルまで</span><b>${lv?.need ? fmt(Math.max(0, lv.need - lv.into)) : '—'}</b></div>
    ${sp ? `<div class="sp" title="スキルのポイント${sp.left != null ? '（残り/合計）' : ''}">${icon('sp')}ポイント<b>${sp.left != null ? `${sp.left}<small>/${sp.total}</small>` : sp.total}</b></div>` : ''}`;
}

function renderTop() {
  const top = $('#topbar')!;
  const me = state.me;
  const logo = `<a class="logo" href="#/top" aria-label="百鬼国盗 トップへ"><img src="/art/ui/logo.webp" alt="百鬼国盗" width="960" height="276"></a>`;
  if (!me) {
    top.innerHTML = logo;
    return;
  }
  const r = state.resources;
  const { lv } = lvData();
  const st = state.stamina;
  // PC：本体/上限＋予備。スマホ（幅が狭い）：使える合計（本体＋予備）だけ。本体が0でも予備で出撃できるので、0と見せない
  const hyoro = st
    ? `<span class="hy-full">${fmt(st.value)}<small>/${fmt(st.max)}</small>${st.reserve ? `<small>+${fmt(st.reserve)}</small>` : ''}</span><span class="hy-total">${fmt(st.value + st.reserve)}</span>`
    : fmt(r.hyoro);
  const unread = { presents: me.unreadPresents ?? 0, news: newsUnread() };
  const anyMenu = unread.presents + unread.news > 0;
  top.innerHTML = `
    ${logo}
    <div class="resbar" role="group" aria-label="所持資源">
      <span class="res" title="銭">${resIcon('zeni')}<span class="rl">銭</span>${resourceValue(r.zeni)}</span>
      <span class="res" title="霊玉">${resIcon('reidama')}<span class="rl">霊玉</span>${resourceValue(r.reidama)}</span>
      <span class="res" title="材木">${resIcon('zaimoku')}<span class="rl">材木</span>${resourceValue(r.zaimoku)}</span>
      <button type="button" class="res res-btn" data-stamina title="兵糧${st?.nextAt ? `（次の回復 ${until(st.nextAt)}）` : ''}" aria-label="兵糧の情報">${resIcon('hyoro')}<span class="rl">兵糧</span><b>${hyoro}</b></button>
    </div>
    <button class="top-lv" data-go="skills" aria-label="主Lv ${lv?.level ?? ''}（スキルへ）">${icon('user')}主Lv.<b>${lv?.level ?? '—'}</b>${icon('right', 'chev')}</button>
    ${state.online ? '' : '<span class="net-off" role="status">オフライン</span>'}
    ${state.pendingOrder ? '<button class="pending-chip" data-go="purchases">購入の反映待ち</button>' : ''}
    <div class="top-icons">
      <button class="icon-btn pc-only" data-go="missions" aria-label="任務">${artIcon('mission')}</button>
      <button class="icon-btn pc-only" data-go="presents" aria-label="プレゼント${unread.presents ? `（${unread.presents}件）` : ''}">${artIcon('present')}${unread.presents ? `<em class="badge">${unread.presents > 99 ? '99+' : unread.presents}</em>` : ''}</button>
      <button class="icon-btn pc-only" data-go="news" aria-label="お知らせ${unread.news ? '（未読あり）' : ''}">${artIcon('bell')}${unread.news ? '<em class="dot"></em>' : ''}</button>
      <button class="icon-btn sp-only chat-btn" data-chatbtn aria-label="全体チャット">${icon('chat')}<em class="badge" ${chatUnread() ? '' : 'hidden'}>${chatUnread() > 99 ? '99+' : chatUnread()}</em></button>
      <button class="icon-btn" data-menu aria-label="メニュー">${icon('menu')}${anyMenu ? '<em class="dot sp-dot"></em>' : ''}</button>
    </div>`;
  void currentName;
}

/** メニュー（右から出る引き出し） */
function openMenu() {
  const me = state.me;
  if (!me) return;
  const sp = mobile();
  const { lv, sp: spv } = lvData();
  const unread = { presents: me.unreadPresents ?? 0, news: newsUnread() };
  // 帯の左にメニューを並べているときは、そこにあるもの（おまもりガチャ・図鑑・物語・番付）は引き出しに出さない
  const items = MENU.filter((m) => (sp || !m.sp) && !(sideNav() && NAV.some((n) => n.id === m.id)));
  const m = openModal(
    `<div class="drawer" role="dialog" aria-label="メニュー">
      <div class="drawer-head"><button class="dh-av" data-avatar aria-label="姿を変える">${avatarHtml(myAvatar(), 52)}</button><div class="grow"><b>${esc(me.user.displayName)}</b>
        <small class="muted">主Lv.${lv?.level ?? '—'}${spv ? `・ポイント ${spv.left != null ? `${spv.left}/${spv.total}` : spv.total}` : ''}</small>
        <div class="meter small"><i style="width:${lv?.need ? Math.min(100, (lv.into / lv.need) * 100) : 100}%"></i></div></div>
        <button class="icon-btn" data-close aria-label="閉じる">${icon('close')}</button></div>
      <ul class="drawer-list">${items
        .map((x) => {
          const badge = x.id === 'presents' && unread.presents ? `<em class="badge">${unread.presents}</em>` : x.id === 'news' && unread.news ? '<em class="dot"></em>' : '';
          return `<li><button data-to="${x.id}">${artIcon(x.icon)}${x.label}${badge}</button></li>`;
        })
        .join('')}</ul>
      ${canSuggestInstall() ? `<button class="btn primary wide drawer-install" data-install>${icon('home')}ホーム画面に追加</button>` : ''}
    </div>`,
    { className: 'drawer-wrap' },
  );
  m.body.addEventListener('click', (e) => {
    if ((e.target as HTMLElement).closest('[data-avatar]')) {
      m.close();
      return openAvatarPicker();
    }
    if ((e.target as HTMLElement).closest('[data-install]')) {
      m.close();
      return void installApp();
    }
    const b = (e.target as HTMLElement).closest<HTMLElement>('[data-to]');
    if (!b) return;
    m.close();
    document.getElementById('app')!.classList.remove('chat-mobile-open');
    if (b.dataset.to === 'contact') return void openContact();
    go(b.dataset.to!);
  });
}

// ---------- お知らせの未読（最新の告知時刻と、最後にお知らせ画面を開いた時刻の比較） ----------
let newsLatest = 0;
export function setNewsLatest(at: number) {
  newsLatest = at;
  renderTop();
}
export function newsUnread() {
  let seen = 0;
  try {
    seen = Number(localStorage.getItem('hk.newsSeen') ?? 0);
  } catch {
    /* 無視 */
  }
  // 答えていないアンケートも未読の扱い（お知らせの先頭に札が出る）
  return newsLatest > seen || (state.me?.survey && !state.me.survey.answered) ? 1 : 0;
}
